/**
 * What an impact is made of, as the renderer understands it.
 *
 * The visual identity of an effect comes from its ImpactViolationType and nothing
 * else. There is no per-skill renderer and no lookup by skill id: the same Heat
 * profile has to serve a fireball, a burning floor, an explosion and an
 * environmental hazard, because all four are the same violation happening to the
 * same ground.
 */

export const VIOLATION_TYPES = [
  'None', 'Physical', 'Mental', 'Energy', 'Heat', 'Cold', 'Light', 'Darkness',
];

/**
 * `Psychic` appears in skill records but never made it into the canonical enum in
 * apps/core/models/core.py. Until the domain enum is settled the renderer draws it
 * through Mental; the raw value stays on the instance for logging.
 *
 * `Ice` and `Elemental` are older school-authoring shorthand (see
 * `apps/school/fixtures/schools/*.json`) for what the canonical enum calls `Cold`
 * and `Energy` — alias them so "Ice Shard" does not render as the grey `None`
 * profile just because the enum was named after the effect and not the sensation.
 *
 * `Water` has no canonical home — it names a school, not a material, and the enum
 * has no elemental slot for it. Every Water School skill that deals damage already
 * resolves to `Cold` in the live fixture (frost, tidal, glacial, arctic — all
 * `Cold`), so `Water` is aliased to `Cold` to match what the content already
 * settled on. This is safe specifically because a heal never reaches this alias:
 * "Healing Rain" carries violation `None` (its `kind` is `Heal`), so mapping
 * `Water` -> `Cold` cannot make a heal read as frost damage.
 */
const ALIASES = {
  psychic: 'Mental',
  ice: 'Cold',
  elemental: 'Energy',
  water: 'Cold',
};

export function normalizeViolationType(value) {
  if (!value) return 'None';
  const raw = String(value).trim();
  const alias = ALIASES[raw.toLowerCase()];
  if (alias) return alias;
  const match = VIOLATION_TYPES.find(type => type.toLowerCase() === raw.toLowerCase());
  return match || 'None';
}

export function isAliasedViolationType(value) {
  return Boolean(value && ALIASES[String(value).trim().toLowerCase()]);
}

/**
 * Stat names that have shown up in the `impact[].type` slot in older skill
 * authoring (`Dodge`, `Speed`, `Accuracy` — see `dx_backend/fixtures/skills_definitions.json`,
 * a snapshot never loaded by `loaddata`). A stat name is not a material: it is the
 * formula's scaling stat, not what the effect is made of. `normalizeViolationType`
 * already falls back to `None` for any string it does not recognise, so this exists
 * to make that fallback an intentional, named case rather than an accident that
 * looks identical to a genuinely unmapped violation.
 *
 * The ten base stats are written out here DELIBERATELY, and are NOT imported from
 * the generated `CharacterStatEnum` in `@/api/dx-backend`. This module is pure
 * rendering — it turns a violation string into colours and a shader branch — and a
 * static import would drag the whole generated API client into the module graph of
 * every board that draws an effect, purely to spell ten words it already knows.
 *
 * The coupling is not free. `@/api/dx-backend` is the module every component spec
 * stubs, and a stub written for a component that only needs `CharacterApi` does not
 * export `CharacterStatEnum` — so deriving the list from it made merely importing
 * this file throw inside those suites, taking three unrelated tactical-map spec
 * files down at import time with an error naming neither this module nor the stat
 * list. That is a lot of blast radius for a list that only decides whether an
 * unrecognised `impact[].type` is reported as "a stat name" or as "unmapped".
 *
 * The drift this trades away is cheap by comparison: renaming a stat merely moves
 * one string from the named case back to the generic `None` fallback, which is
 * already the behaviour for any unknown value — nothing renders wrong, and
 * `impactViolation.spec.js` pins this list against the enum so the rename is caught
 * in the one place that can afford to know about both.
 */
const STAT_NAMES = new Set([
  'physical strength', 'mental strength', 'flow resonance', 'concentration',
  'flow manipulation', 'flow connection', 'knowledge', 'speed', 'luck', 'charisma',
  'dodge', 'accuracy',
]);

export function isStatNameNotViolation(value) {
  return Boolean(value && STAT_NAMES.has(String(value).trim().toLowerCase()));
}

/**
 * One profile per violation: the colours the material mixes between, the shader
 * branch it takes, and whether it adds light to the board or sits on top of it.
 *
 * `mode` is the branch index shared by the decal, volume and particle shaders.
 * `edgeNoise` is how far the boundary may wander off the ideal shape, in cells:
 * fire and frost eat into ground unevenly, a field of light does not.
 *
 * Three colours, not two, and the third is the one that does the work. The board is
 * near-black and the figures on it are saturated holograms; an effect painted in a
 * mid-tone reads as a sticker laid over the picture. `core` is the emitting centre —
 * white-hot, near-unsaturated — `color` is the material read at its own temperature,
 * and `deep` is where it cools out to. Every trace, spark and impact bloom runs that
 * ramp from the inside out, which is what makes it read as LIGHT rather than paint.
 *
 * `decalAdditive` is separate from `additive` because the ground and the light above
 * it are not the same job. Physical scorches and dusts the floor — a subtractive,
 * low-chroma event — while the sparks it throws are the brightest thing on screen.
 * Darkness is the same argument inverted: the body takes light off the board and
 * only its rim emits.
 *
 * The GM analytics palette in types/skillVisualization.ts is deliberately muted —
 * it colours bar charts on a parchment panel. These are the same violations lit on
 * a dark board, so they are the vivid siblings of those swatches, not the same
 * values.
 */
export const VIOLATION_PROFILES = {
  /* Deliberate and cold-spectral: an unattributed effect is still an effect. */
  None: {
    edgeNoise: 0.14,
    mode: 0, color: '#6FE7D4', deep: '#0E4A46', core: '#E8FFFA', additive: true,
    decalAdditive: true,
    light: '#8ff0e0', lightStrength: 0.6, volume: null,
  },
  /* Grit, dust and hot sparks: low chroma on the ground, high contrast in the air. */
  Physical: {
    edgeNoise: 0.45,
    mode: 1, color: '#B9A895', deep: '#1A1613', core: '#FFE7C0', additive: true,
    decalAdditive: false,
    light: '#ffc98a', lightStrength: 0.7, volume: 'shape', grit: true,
  },
  Mental: {
    edgeNoise: 0.3,
    mode: 2, color: '#E066FF', deep: '#3A1470', core: '#FFDDFF', additive: true,
    decalAdditive: true,
    light: '#d08cff', lightStrength: 0.8, volume: 'shape',
  },
  Energy: {
    edgeNoise: 0.2,
    mode: 3, color: '#5AE8FF', deep: '#0B3AAE', core: '#F4FFFF', additive: true,
    decalAdditive: true,
    light: '#66d0ff', lightStrength: 1.0, volume: 'shape',
  },
  Heat: {
    edgeNoise: 0.52,
    mode: 4, color: '#FF8F2E', deep: '#A81704', core: '#FFF0C6', additive: true,
    decalAdditive: true,
    light: '#ff8a3d', lightStrength: 1.0, volume: 'shape',
  },
  Cold: {
    edgeNoise: 0.4,
    mode: 5, color: '#B6EEFF', deep: '#2276BE', core: '#FFFFFF', additive: true,
    decalAdditive: true,
    light: '#bfe6ff', lightStrength: 0.6, volume: 'shape', crystals: true,
  },
  Light: {
    edgeNoise: 0.22,
    mode: 6, color: '#FFE49A', deep: '#D0901E', core: '#FFFFFF', additive: true,
    decalAdditive: true,
    light: '#fff0b8', lightStrength: 1.0, volume: 'shape', rays: true,
  },
  /* A dark core is invisible on a dark board. The rim is what carries it. */
  Darkness: {
    edgeNoise: 0.58,
    mode: 7, color: '#9463FF', deep: '#070412', core: '#D9BEFF', additive: false,
    decalAdditive: false,
    light: null, lightStrength: 0, volume: 'shape', halo: true,
  },
};

/**
 * The same material in two different hands.
 *
 * A path may not change WHAT an effect is made of — Energy is Energy on both sides,
 * and every profile above stands — but it does change how that material behaves at
 * its boundary. Something emitted by a mechanism has an edge a machine could have
 * cut; something drawn through the world has an edge that wanders. That is one
 * number, `edgeNoise`, and it is the whole of the difference here: the colours, the
 * shader branch and the light are untouched, so a Technomancer's fire is still fire.
 *
 * `lightStrength` moves a little with it — an emitter is aimed, so it throws a
 * harder light than the same material bled into the air.
 */
const PATH_MATERIAL = {
  john: { edgeNoise: 0.45, lightStrength: 1.12 },
  json: { edgeNoise: 1.25, lightStrength: 0.95 },
};

const pathProfiles = new Map();

/**
 * The profile a violation renders through, optionally in one path's hand.
 *
 * Called with one argument everywhere the path is not known, which is every caller
 * today — the second argument is the seam the renderer needs to read `cast.path`,
 * and it is deliberately additive so that turning it on is one word per call site.
 */
export function violationProfile(value, path = null) {
  const base = VIOLATION_PROFILES[normalizeViolationType(value)];
  const material = PATH_MATERIAL[path];
  if (!material) return base;
  const key = `${normalizeViolationType(value)}:${path}`;
  if (!pathProfiles.has(key)) {
    pathProfiles.set(key, {
      ...base,
      edgeNoise: base.edgeNoise * material.edgeNoise,
      lightStrength: base.lightStrength * material.lightStrength,
    });
  }
  return pathProfiles.get(key);
}

export const FOOTPRINT_HINTS = ['single', 'line', 'burst', 'ring', 'cone', 'cluster'];

export const FOOTPRINT_IDS = {
  single: 0, line: 1, burst: 2, ring: 3, cone: 4, cluster: 5,
};

/**
 * `rounded-cells` is the fight's mode, and the reason it exists:
 *
 * a fight is adjudicated on cells, so what an effect claims must be exactly what
 * the server said it hit — but a square is a bookkeeping artifact, not a thing that
 * happens to ground. Rounded keeps the authoritative union and softens its corners
 * from the inside, so the effect can never overstate its reach and never looks like
 * a spreadsheet. `strict-cells` remains, unsoftened, for checking exactness.
 */
export const MERGE_MODES = ['strict-cells', 'rounded-cells', 'ideal-shape', 'clipped-shape'];

export const MERGE_IDS = {
  'strict-cells': 0, 'ideal-shape': 1, 'clipped-shape': 2, 'rounded-cells': 3,
};

export const PHASES = ['preview', 'execute', 'persist', 'expire'];

export const PHASE_DURATION_MS = {
  preview: 900,
  execute: 1400,
  persist: 2400,
  expire: 1300,
};

export const QUALITY_PROFILES = {
  high: { particles: 520, dpr: 2, octaves: 5, sdfPixelsPerCell: 20 },
  medium: { particles: 300, dpr: 1.5, octaves: 4, sdfPixelsPerCell: 14 },
  low: { particles: 150, dpr: 1, octaves: 3, sdfPixelsPerCell: 10 },
};
